import React, { useState } from "react";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowRight, Eye, EyeOff, User, Phone } from "lucide-react";
import AppLogo from "components/common/AppLogo";
import { AppButton } from "components/misc/Buttons";
import { ROUTES } from "constants/routes";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C_PRIMARY = "#ff6c00";
const C_SECONDARY = "#111111";

// ─── Animation ───────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut", delay: i * 0.1 },
  }),
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
  global?: string;
}

// ─── Styled ──────────────────────────────────────────────────────────────────

const Page = styled.div`
  display: flex;
  min-height: 100vh;
`;

// ── Left branding panel ──

const BrandPanel = styled.div`
  display: none;
  flex-direction: column;
  justify-content: space-between;
  padding: 3rem 3.5rem;
  background: ${C_SECONDARY};
  width: 42%;
  flex-shrink: 0;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const BrandBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4rem 0;
`;

const BrandEyebrow = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: ${C_PRIMARY};
  margin-bottom: 1rem;
`;

const BrandHeading = styled.h2`
  font-size: 2rem;
  font-weight: 900;
  color: white;
  line-height: 1.15;
  margin-bottom: 1.25rem;
`;

const BrandDesc = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.75;
  max-width: 26rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.6);
`;

const FeatureDot = styled.span`
  width: 6px;
  height: 6px;
  background: ${C_PRIMARY};
  flex-shrink: 0;
  display: inline-block;
`;

const BrandFooter = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.2);
`;

// ── Right form panel ──

const FormPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem 1.5rem;
  background: #fafafa;
  overflow-y: auto;
`;

const FormBox = styled.div`
  width: 100%;
  max-width: 28rem;
`;

const MobileLogoWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const FormEyebrow = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${C_PRIMARY};
  margin-bottom: 0.6rem;
`;

const FormHeading = styled.h1`
  font-size: 1.65rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 0.5rem;
`;

const FormSub = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin-bottom: 2rem;
  padding-bottom: 1.75rem;
  border-bottom: 1px solid #e5e7eb;
`;

const NameRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #374151;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  display: flex;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 2.75rem;
  border: 1.5px solid ${(p) => (p.$hasError ? "#ef4444" : "#e5e7eb")};
  background: white;
  font-size: 0.875rem;
  color: ${C_SECONDARY};
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${(p) => (p.$hasError ? "#ef4444" : C_PRIMARY)};
  }

  &::placeholder {
    color: #c1c1c1;
  }
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  padding: 0;

  &:hover {
    color: ${C_SECONDARY};
  }
`;

const ErrorMsg = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  color: #ef4444;
`;

const GlobalError = styled.div`
  padding: 0.9rem 1rem;
  background: #fef2f2;
  border-left: 3px solid #ef4444;
  margin-bottom: 1.25rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #b91c1c;
`;

const LoginLink = styled.p`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.82rem;
  color: #6b7280;

  a {
    color: ${C_PRIMARY};
    font-weight: 700;
    text-decoration: none;
    margin-left: 0.3rem;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const TermsNotice = styled.p`
  font-size: 0.72rem;
  color: #9ca3af;
  text-align: center;
  margin-top: 1.25rem;
  line-height: 1.6;

  a {
    color: ${C_PRIMARY};
    font-weight: 700;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const validate = (f: FormState): FormErrors => {
  const e: FormErrors = {};
  if (!f.firstName.trim()) e.firstName = "Le prénom est requis.";
  if (!f.lastName.trim()) e.lastName = "Le nom est requis.";
  if (!f.email) {
    e.email = "L'e-mail est requis.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) {
    e.email = "Format d'e-mail invalide.";
  }
  if (!f.phone.trim()) e.phone = "Le téléphone est requis.";
  if (!f.password) {
    e.password = "Le mot de passe est requis.";
  } else if (f.password.length < 8) {
    e.password = "Minimum 8 caractères.";
  }
  if (!f.confirmPassword) {
    e.confirmPassword = "Veuillez confirmer votre mot de passe.";
  } else if (f.confirmPassword !== f.password) {
    e.confirmPassword = "Les mots de passe ne correspondent pas.";
  }
  return e;
};

// ─── Component ────────────────────────────────────────────────────────────────

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const set =
    (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
      if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(form);
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
    setLoading(true);
    try {
      // TODO: appel API inscription
      await new Promise((r) => setTimeout(r, 1500));
      navigate(ROUTES.LOGIN);
    } catch {
      setErrors({ global: "Une erreur est survenue. Veuillez réessayer." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      {/* ── Brand panel ── */}
      <BrandPanel>
        <AppLogo variant="dark" size="md" href="/" />
        <BrandBody>
          <BrandEyebrow>Rejoignez MomsEduc Study</BrandEyebrow>
          <BrandHeading>Votre aventure académique commence ici.</BrandHeading>
          <BrandDesc>
            Créez votre compte en quelques minutes et accédez à tous nos
            services d'accompagnement pour votre mobilité internationale.
          </BrandDesc>
          <FeatureList>
            {[
              "Attestation AVI en 48h ouvrées",
              "Accompagnement visa & logement",
              "Suivi de dossier en temps réel",
              "Équipe disponible 7j/7",
            ].map((f) => (
              <FeatureItem key={f}>
                <FeatureDot />
                {f}
              </FeatureItem>
            ))}
          </FeatureList>
        </BrandBody>
        <BrandFooter>
          © {new Date().getFullYear()} MomsEduc Study. Tous droits réservés.
        </BrandFooter>
      </BrandPanel>

      {/* ── Form panel ── */}
      <FormPanel>
        <FormBox>
          <MDiv variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <MobileLogoWrap>
              <AppLogo variant="light" size="md" href="/" />
            </MobileLogoWrap>
          </MDiv>

          <MDiv variants={fadeUp} initial="hidden" animate="visible" custom={1}>
            <FormEyebrow>Créer un compte</FormEyebrow>
            <FormHeading>Inscription</FormHeading>
            <FormSub>
              Déjà inscrit ?{" "}
              <Link
                to={ROUTES.LOGIN}
                style={{
                  color: C_PRIMARY,
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                Se connecter
              </Link>
            </FormSub>
          </MDiv>

          <MDiv variants={fadeUp} initial="hidden" animate="visible" custom={2}>
            <form onSubmit={handleSubmit} noValidate>
              {errors.global && <GlobalError>{errors.global}</GlobalError>}

              {/* ── Nom / Prénom ── */}
              <NameRow>
                <FieldGroup>
                  <Label htmlFor="reg-firstname">Prénom</Label>
                  <InputWrapper>
                    <InputIcon>
                      <User size={15} />
                    </InputIcon>
                    <Input
                      id="reg-firstname"
                      type="text"
                      placeholder="Jean"
                      value={form.firstName}
                      onChange={set("firstName")}
                      $hasError={!!errors.firstName}
                      autoComplete="given-name"
                    />
                  </InputWrapper>
                  {errors.firstName && <ErrorMsg>{errors.firstName}</ErrorMsg>}
                </FieldGroup>

                <FieldGroup>
                  <Label htmlFor="reg-lastname">Nom</Label>
                  <InputWrapper>
                    <InputIcon>
                      <User size={15} />
                    </InputIcon>
                    <Input
                      id="reg-lastname"
                      type="text"
                      placeholder="Dupont"
                      value={form.lastName}
                      onChange={set("lastName")}
                      $hasError={!!errors.lastName}
                      autoComplete="family-name"
                    />
                  </InputWrapper>
                  {errors.lastName && <ErrorMsg>{errors.lastName}</ErrorMsg>}
                </FieldGroup>
              </NameRow>

              {/* ── Email ── */}
              <FieldGroup>
                <Label htmlFor="reg-email">Adresse e-mail</Label>
                <InputWrapper>
                  <InputIcon>
                    <Mail size={15} />
                  </InputIcon>
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="jean.dupont@exemple.com"
                    value={form.email}
                    onChange={set("email")}
                    $hasError={!!errors.email}
                    autoComplete="email"
                  />
                </InputWrapper>
                {errors.email && <ErrorMsg>{errors.email}</ErrorMsg>}
              </FieldGroup>

              {/* ── Téléphone ── */}
              <FieldGroup>
                <Label htmlFor="reg-phone">Téléphone / WhatsApp</Label>
                <InputWrapper>
                  <InputIcon>
                    <Phone size={15} />
                  </InputIcon>
                  <Input
                    id="reg-phone"
                    type="tel"
                    placeholder="+237 6XX XXX XXX"
                    value={form.phone}
                    onChange={set("phone")}
                    $hasError={!!errors.phone}
                    autoComplete="tel"
                  />
                </InputWrapper>
                {errors.phone && <ErrorMsg>{errors.phone}</ErrorMsg>}
              </FieldGroup>

              {/* ── Mot de passe ── */}
              <FieldGroup>
                <Label htmlFor="reg-password">Mot de passe</Label>
                <InputWrapper>
                  <InputIcon>
                    <Lock size={15} />
                  </InputIcon>
                  <Input
                    id="reg-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Minimum 8 caractères"
                    value={form.password}
                    onChange={set("password")}
                    $hasError={!!errors.password}
                    autoComplete="new-password"
                  />
                  <ToggleBtn
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword
                        ? "Masquer le mot de passe"
                        : "Afficher le mot de passe"
                    }
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </ToggleBtn>
                </InputWrapper>
                {errors.password && <ErrorMsg>{errors.password}</ErrorMsg>}
              </FieldGroup>

              {/* ── Confirmation ── */}
              <FieldGroup>
                <Label htmlFor="reg-confirm">Confirmer le mot de passe</Label>
                <InputWrapper>
                  <InputIcon>
                    <Lock size={15} />
                  </InputIcon>
                  <Input
                    id="reg-confirm"
                    type={showConfirm ? "text" : "password"}
                    placeholder="Répétez votre mot de passe"
                    value={form.confirmPassword}
                    onChange={set("confirmPassword")}
                    $hasError={!!errors.confirmPassword}
                    autoComplete="new-password"
                  />
                  <ToggleBtn
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={
                      showConfirm
                        ? "Masquer la confirmation"
                        : "Afficher la confirmation"
                    }
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </ToggleBtn>
                </InputWrapper>
                {errors.confirmPassword && (
                  <ErrorMsg>{errors.confirmPassword}</ErrorMsg>
                )}
              </FieldGroup>

              {/* ── Submit ── */}
              <div style={{ marginTop: "1.5rem" }}>
                <AppButton
                  type="submit"
                  size="lg"
                  fullWidth
                  loading={loading}
                  iconRight={<ArrowRight size={16} />}
                >
                  Créer mon compte
                </AppButton>
              </div>

              <TermsNotice>
                En créant votre compte, vous acceptez nos{" "}
                <Link to={ROUTES.CGV}>CGV</Link> et notre{" "}
                <Link to={ROUTES.PRIVACY_POLICY}>
                  politique de confidentialité
                </Link>
                .
              </TermsNotice>

              <LoginLink>
                Déjà un compte ?<Link to={ROUTES.LOGIN}>Se connecter</Link>
              </LoginLink>
            </form>
          </MDiv>
        </FormBox>
      </FormPanel>
    </Page>
  );
};

export default RegisterPage;
